import { useEffect, useMemo, useState } from 'react'
import { Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Button, Card, EmptyState, KeyValueList, PillTone, Spinner, StatusPill } from '../../../ui'
import { ReplyDetailPane, CategoryMeta } from '../commsInboxView/ReplyDetailPane'
import { SentEmailPane } from '../commsInboxView/SentEmailModal'
import { LetterTypePill, formatDateTime, humanise, sendKindLabel, sendStatusTone } from '../commsInboxView/shared'
import { MailboxRef } from '../commsShared/mailboxes'
import { COUNTERPARTY_LABEL, ConversationDetail, STATE_LABEL, ThreadSubcategoryLabel, TrailMessage } from './types'
import { EVIDENCE_LABEL, FLAG_LABEL } from '../commsShared/deepDive'
import styles from './styles.module.scss'

interface Props {
    conversationId: string | null
    /** Open with this message expanded (deep link). Either kind of message. */
    initialMessageId?: string | null
    categoryMeta?: Map<string, CategoryMeta>
    mailboxes?: MailboxRef[]
    /** Fired after an action inside the reply pane changed an email (so lists can refresh). */
    onChanged?: () => void
    onShowAllOnClaim?: (claimRef: string) => void
    onSelectedMessage?: (messageId: string | null) => void
}

export const stateTone = (state: string): PillTone => (state === 'awaiting_us' ? 'warn' : state === 'closed' ? 'neutral' : state === 'unknown' ? 'neutral' : 'info')

const roleLabel = (m: TrailMessage) => {
    if (m.kind === 'outbound') return m.role === 'letter' ? `We sent: ${sendKindLabel(m.sendKind || 'initial_loc').toLowerCase()}` : 'We sent'
    if (m.role === 'our_reply') return 'Our reply'
    if (m.role === 'forward') return 'Forwarded'
    return 'Reply received'
}

/**
 * Newest first, on the same date the row displays. The API orders by
 * comms_conversation_message.position, which is renumbered from that table's
 * own sent_at and can disagree with the date shown here.
 */
const byNewestFirst = (a: TrailMessage, b: TrailMessage) => {
    const at = a.at ? new Date(a.at).getTime() : NaN
    const bt = b.at ? new Date(b.at).getTime() : NaN
    if (Number.isNaN(at) && Number.isNaN(bt)) return b.position - a.position
    if (Number.isNaN(at)) return 1
    if (Number.isNaN(bt)) return -1
    return bt - at
}

const TIMING_TEXT: Record<string, string> = { now: 'now', within_7_days: 'within 7 days', within_14_days: 'within 14 days', in_4_weeks: 'in 4 weeks', in_6_weeks: 'in 6 weeks', in_8_weeks: 'in 8 weeks', on_reply: 'when they reply' }

/**
 * The deep dive's reading of this thread's outcome email: the sub-category,
 * what exactly was said, and the email's own wording that supports it.
 */
const DeepDiveLabelCard = ({ label, parentLabel, onOpenEmail }: { label: ThreadSubcategoryLabel; parentLabel: string | null; onOpenEmail: (id: string) => void }) => {
    const ev = EVIDENCE_LABEL[label.evidenceState] ?? { text: label.evidenceState, hint: '' }
    const action = label.subcategory.suggestedAction
    const quotes = label.evidence.filter((e) => e.quote)
    return (
        <Card
            padding="sm"
            title={
                <>
                    {parentLabel ? `${parentLabel} · ` : ''}
                    {label.subcategory.label}
                </>
            }
            subtitle={label.labelledBy?.startsWith('human:') ? `Placed here by ${label.labelledBy.slice(6)} · ${formatDateTime(label.labelledAt)}` : `Deep dive · ${label.model ?? 'model'} · ${formatDateTime(label.labelledAt)}${label.confidence != null ? ` · ${label.confidence}% confident` : ''}`}
            actions={
                <div className={styles.chips}>
                    <StatusPill size="sm" tone={label.evidenceState === 'verified' ? 'good' : label.evidenceState === 'needs_check' ? 'warn' : 'accent'} title={ev.hint}>
                        {ev.text}
                    </StatusPill>
                    {label.flags.map((f) => (
                        <StatusPill key={f} size="sm" tone={f === 'injection_suspect' ? 'bad' : 'warn'} title={FLAG_LABEL[f]?.hint}>
                            {FLAG_LABEL[f]?.text ?? f.replace(/_/g, ' ')}
                        </StatusPill>
                    ))}
                </div>
            }
        >
            {label.subcategory.description && <p className={styles.muted}>{label.subcategory.description}</p>}
            {label.details.length > 0 && (
                <ul className={styles.deepDiveDetails}>
                    {label.details.map((d, i) => (
                        <li key={i}>{d}</li>
                    ))}
                </ul>
            )}
            {quotes.length > 0 && (
                <div className={styles.deepDiveQuotes}>
                    {quotes.map((e, i) => (
                        <blockquote key={i} className={`${styles.deepDiveQuote} ${e.verified ? '' : styles.deepDiveQuoteUnverified}`} title={e.verified ? 'Found word for word in the email' : 'Not found in the email as written'}>
                            “{e.quote}”{!e.verified && <span className={styles.deepDiveQuoteWarn}> not found in the email</span>}
                        </blockquote>
                    ))}
                </div>
            )}
            {action?.label && (
                <p className={styles.nextStep}>
                    <strong>Suggested action:</strong> {action.label}
                    {action.timing ? ` · ${TIMING_TEXT[action.timing] ?? action.timing.replace(/_/g, ' ')}` : ''}
                    {action.description ? <span className={styles.muted}> — {action.description}</span> : null}
                </p>
            )}
            {label.outcomeInboundId && (
                <Button size="sm" variant="link" onClick={() => onOpenEmail(label.outcomeInboundId!)} title="Open the email this reading is based on; the quoted wording is highlighted">
                    Open the email it was read from →
                </Button>
            )}
        </Card>
    )
}

/**
 * One conversation end to end: the letter we sent, every reply, our own
 * follow-ups, newest first. Click any email to open it in place: a received
 * one to work it (category, link, facts, body), a sent one to read what went
 * out and its delivery trail. Threads load collapsed.
 */
export const ConversationTrail = ({ conversationId, initialMessageId = null, categoryMeta, mailboxes, onChanged, onShowAllOnClaim, onSelectedMessage }: Props) => {
    const [detail, setDetail] = useState<ConversationDetail | null>(null)
    const [loading, setLoading] = useState(false)
    const [openId, setOpenId] = useState<string | null>(initialMessageId)
    const [version, setVersion] = useState(0)
    const [summarising, setSummarising] = useState(false)

    const summariseNow = async () => {
        if (!conversationId) return
        setSummarising(true)
        try {
            const resp = await api({ url: 'comms/inbox/conversation-summarise', data: { conversationId } })
            if (resp.data?.success) setVersion((v) => v + 1)
            else Report.info('Summary', resp.data?.message || 'Nothing was written.', 'Okay')
        } catch (err: any) {
            Report.failure('Summary failed', err?.response?.data?.message || 'Please try again.', 'Okay')
        } finally {
            setSummarising(false)
        }
    }

    useEffect(() => {
        setOpenId(initialMessageId)
    }, [conversationId, initialMessageId])

    useEffect(() => {
        if (!conversationId) {
            setDetail(null)
            return
        }
        let cancelled = false
        setLoading(true)
        api({ url: 'comms/inbox/conversation', data: { conversationId } })
            .then((resp) => {
                if (!cancelled && resp.data?.success) setDetail(resp.data)
            })
            .catch((err: any) => {
                if (!cancelled) Report.failure('Load failed', err?.response?.data?.message || 'Could not load the conversation.', 'Okay')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [conversationId, version])

    const pick = (id: string | null) => {
        setOpenId(id)
        onSelectedMessage?.(id)
    }

    const messages = useMemo(() => [...(detail?.messages ?? [])].sort(byNewestFirst), [detail])
    /** Deep dive quotes per email id, so the outcome email can mark the wording it was read from. */
    const highlightsByEmail = useMemo(() => {
        const map = new Map<string, string[]>()
        for (const l of detail?.subcategories ?? []) {
            for (const e of l.evidence) {
                const id = e.inboundId ?? l.outcomeInboundId
                if (!id || !e.quote) continue
                map.set(id, [...(map.get(id) ?? []), e.quote])
            }
        }
        return map
    }, [detail])
    const labelByEmail = useMemo(() => new Map((detail?.subcategories ?? []).filter((l) => l.outcomeInboundId).map((l) => [l.outcomeInboundId as string, l])), [detail])

    if (!conversationId) return <EmptyState bare title="Select a conversation" description="Pick a conversation to see the letter we sent, every reply and our own follow-ups, newest first." />
    if (loading && !detail) return <Spinner block text="Loading conversation…" />
    if (!detail) return null

    const c = detail.conversation
    const categoryLabel = (key: string | null) => (key ? categoryMeta?.get(key)?.label ?? humanise(key) : null)
    const identityItems = detail.identity
        ? [
              { label: 'Client', value: detail.identity.clientName ?? '—' },
              { label: 'Email', value: detail.identity.clientEmail ?? '—' },
              { label: 'Lender', value: detail.identity.lenderName ?? c.lenderName ?? '—' },
              { label: 'Agreement', value: detail.identity.agreementNumber ?? '—', mono: true },
              { label: 'Vehicle', value: detail.identity.vehicle ? `${detail.identity.vehicle}${detail.identity.vehicleRegistration ? ` · ${detail.identity.vehicleRegistration}` : ''}` : detail.identity.vehicleRegistration ?? '—' },
              { label: 'Introducer', value: detail.identity.introducer ?? '—' },
          ]
        : []

    return (
        <div className={styles.trail}>
            <div className={styles.trailHeader}>
                <div className={styles.trailHeaderMain}>
                    <h2 className={styles.trailTitle}>{c.subject || '(no subject)'}</h2>
                    <div className={styles.trailSub}>
                        <StatusPill tone={stateTone(c.state)} dot size="sm">
                            {STATE_LABEL[c.state]}
                        </StatusPill>
                        {c.outcomeCategory ? (
                            <StatusPill size="sm" tone="info" title={`Where the thread stands: the newest email that is not automated mail${c.outcomeAt ? ` (${formatDateTime(c.outcomeAt)})` : ''}`}>
                                {categoryLabel(c.outcomeCategory)}
                            </StatusPill>
                        ) : c.inboundCount > 0 && c.automatedCount >= c.inboundCount ? (
                            <StatusPill size="sm" title="Every email in this thread is automated mail">
                                automated mail only
                            </StatusPill>
                        ) : null}
                        {c.automatedCount > 0 && c.automatedCount < c.inboundCount && (
                            <span className={styles.muted} title="Bounces, auto replies and sales mail in this thread; they never move the outcome">
                                {c.automatedCount} automated
                            </span>
                        )}
                        <span>
                            {COUNTERPARTY_LABEL[c.counterpartyKind]}
                            {c.lenderName ? <strong> {c.lenderName}</strong> : null}
                            {c.counterpartyAddress ? <span className={styles.mono}> {c.counterpartyAddress}</span> : null}
                        </span>
                        {c.claimRef ? (
                            <Button size="sm" variant="link" className={styles.mono} onClick={() => onShowAllOnClaim?.(c.claimRef!)} title="Show every conversation on this claim">
                                {c.claimRef}
                            </Button>
                        ) : (
                            <span className={styles.muted}>No claim linked</span>
                        )}
                        <span className={styles.muted}>
                            {c.messageCount} message{c.messageCount === 1 ? '' : 's'} · {formatDateTime(c.firstMessageAt)} → {formatDateTime(c.lastMessageAt)}
                        </span>
                        {c.ownerKind === 'agent' && <StatusPill size="sm" tone="accent">agent-owned</StatusPill>}
                    </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setVersion((v) => v + 1)} disabled={loading}>
                    Refresh
                </Button>
            </div>

            {(c.summary || c.suggestedNextAction || detail.threadSummaryEnabled) && (
                <Card
                    padding="sm"
                    title="Where this thread is"
                    subtitle={c.summaryAt ? `Summarised ${formatDateTime(c.summaryAt)}` : detail.threadSummaryEnabled ? 'Not summarised yet' : undefined}
                    actions={
                        detail.threadSummaryEnabled ? (
                            <Button size="sm" variant="ghost" onClick={summariseNow} loading={summarising} disabled={loading || summarising} title="One small-model call; rewrites the summary from the current trail">
                                {c.summary ? 'Regenerate' : 'Summarise now'}
                            </Button>
                        ) : undefined
                    }
                >
                    {c.summary ? <p className={styles.summaryText}>{c.summary}</p> : <p className={styles.muted}>No summary yet. The worker writes one once the thread has a reply; or press Summarise now.</p>}
                    {c.suggestedNextAction && (
                        <p className={styles.nextStep}>
                            <strong>Suggested next step:</strong> {c.suggestedNextAction}
                        </p>
                    )}
                </Card>
            )}

            {(detail.subcategories ?? []).map((l) => (
                <DeepDiveLabelCard key={`${l.parentKey}:${l.subcategory.id}`} label={l} parentLabel={categoryLabel(l.parentKey)} onOpenEmail={(id) => pick(id)} />
            ))}

            {identityItems.length > 0 && (
                <Card padding="sm" title="Client and claim" subtitle={detail.cases.length ? `${detail.cases.length} comms case${detail.cases.length === 1 ? '' : 's'} on ${c.claimRef}` : undefined}>
                    <KeyValueList columns items={identityItems} />
                </Card>
            )}

            <div className={styles.muted}>Newest first. Click an email to open it in place, or click it again to collapse.</div>

            <ol className={styles.timeline}>
                {messages.map((m) => {
                    const ours = m.kind === 'outbound' || m.role === 'our_reply'
                    const active = m.id === openId
                    return (
                        <li key={`${m.kind}:${m.id}`} className={`${styles.timelineItem} ${ours ? styles.timelineOurs : styles.timelineTheirs} ${active ? styles.timelineActive : ''}`}>
                            <button
                                type="button"
                                className={styles.timelineButton}
                                onClick={() => pick(active ? null : m.id)}
                                title={active ? 'Collapse' : m.kind === 'outbound' ? 'Read the email we sent' : 'Open this email'}
                            >
                                <div className={styles.timelineTop}>
                                    <span className={styles.timelineRole}>{roleLabel(m)}</span>
                                    <span className={styles.timelineWhen}>{formatDateTime(m.at)}</span>
                                </div>
                                <div className={styles.timelineWho}>
                                    <span>{m.fromEmail || '—'}</span>
                                    {m.toEmail && <span className={styles.muted}> → {m.toEmail}</span>}
                                </div>
                                <div className={styles.timelineSubject}>{m.subject || '(no subject)'}</div>
                                {m.kind === 'inbound' && m.aiSummary && m.role !== 'our_reply' && <div className={styles.timelineSummary}>{m.aiSummary}</div>}
                                <div className={styles.timelineMeta}>
                                    {m.kind === 'outbound' ? (
                                        <>
                                            <StatusPill size="sm" tone={sendStatusTone(m.status)}>
                                                {humanise(m.status)}
                                            </StatusPill>
                                            {m.lenderName && <span className={styles.muted}>{m.lenderName}</span>}
                                        </>
                                    ) : (
                                        <>
                                            {m.role === 'our_reply' ? (
                                                <StatusPill size="sm">our own email</StatusPill>
                                            ) : m.extractionState === 'done' || m.extractionState === 'skipped' ? (
                                                <StatusPill size="sm" tone={m.classification ? 'info' : 'neutral'}>
                                                    {categoryLabel(m.classification) ?? 'not categorised'}
                                                </StatusPill>
                                            ) : (
                                                <StatusPill size="sm" tone={m.extractionState === 'failed' ? 'bad' : 'neutral'}>
                                                    {m.extractionState === 'failed' ? 'could not be read' : 'not yet read'}
                                                </StatusPill>
                                            )}
                                            {m.role !== 'our_reply' && labelByEmail.get(m.id) && (
                                                <StatusPill size="sm" tone="accent" title="The deep dive read this email; open it to see the quoted wording highlighted">
                                                    {labelByEmail.get(m.id)!.subcategory.label}
                                                </StatusPill>
                                            )}
                                            {m.role !== 'our_reply' && m.matchedSendId && <LetterTypePill sendKind={m.sendKind ?? 'initial_loc'} />}
                                            {m.role !== 'our_reply' && !m.matchedSendId && m.caseId && (
                                                <StatusPill size="sm" tone="good" title="We know the client and claim; the email is not about one specific letter of ours.">
                                                    client and claim known
                                                </StatusPill>
                                            )}
                                            {m.role !== 'our_reply' && !m.caseId && m.linkState === 'candidates' && (
                                                <StatusPill size="sm" tone="warn" title="More than one possible client; open the email to choose.">
                                                    which client?
                                                </StatusPill>
                                            )}
                                            {m.attachmentCount > 0 && (
                                                <StatusPill size="sm">
                                                    {m.attachmentCount} attachment{m.attachmentCount === 1 ? '' : 's'}
                                                </StatusPill>
                                            )}
                                            {m.triageState !== 'pending' && <StatusPill size="sm">{humanise(m.triageState)}</StatusPill>}
                                        </>
                                    )}
                                </div>
                            </button>
                            {active && (
                                <div className={styles.timelineDetail}>
                                    {m.kind === 'outbound' ? (
                                        <SentEmailPane sendId={m.id} />
                                    ) : (
                                        <ReplyDetailPane
                                            replyId={m.id}
                                            classifications={detail.classifications}
                                            categoryMeta={categoryMeta}
                                            mailboxes={mailboxes}
                                            onChanged={() => {
                                                onChanged?.()
                                            }}
                                            onShowAllEmails={onShowAllOnClaim}
                                            onSelectReply={(id) => pick(id)}
                                            highlights={highlightsByEmail.get(m.id) ?? []}
                                        />
                                    )}
                                </div>
                            )}
                        </li>
                    )
                })}
            </ol>

            {detail.participants.length > 0 && (
                <div className={styles.participants}>
                    <span className={styles.muted}>People on this thread:</span>
                    {detail.participants.map((p) => (
                        <StatusPill key={p.address} size="sm" tone={p.role === 'us' ? 'accent' : p.role === 'lender' ? 'info' : p.role === 'client' ? 'good' : 'neutral'} title={`${p.role} · ${p.messageCount} message(s)`}>
                            {p.displayName || p.address}
                        </StatusPill>
                    ))}
                </div>
            )}
        </div>
    )
}
